import Grid from '@mui/material/Grid'
import { BULLETIN_BOARD_THREADS_QUERY_KEY } from 'api/bulletinBoard/bulletinBoard.api'
import { getDisplayName } from 'helpers/global.helper/global.helper'
import { queryClient } from 'helpers/queryClient'
import React, { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Step } from 'react-joyride'
import { Card } from 'ui/atoms'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import BulletinBoard from 'ui/components/BulletinBoard/BulletinBoardView'
import EventLogger from 'ui/components/EventLogger/EventLogger'
import ModuleHeader from 'ui/components/ModuleHeader/ModuleHeader'
import { useChannel, useEvent } from 'ui/components/PusherContext/PusherContext'
import SystemMessageBanner from 'ui/components/SystemMessageBanner/SystemMessageBanner'
import Tour from 'ui/components/Tour/Tour'
import View from 'ui/components/View/View'
import StartpageHeaderContent from './components/StartpageHeaderContent/StartpageHeaderContent'

const widgetConstants = {
  STARTPAGE_CALENDAR: `startpage-calendar`,
  STARTPAGE_OVERVIEW_CHART: `startpage-overview-chart`,
  STARTPAGE_EVENTLOG: `startpage-eventlog`,
  STARTPAGE_BULLETINBOARD: `startpage-bulletin-board`,
  STARTPAGE_CALENDAR_CONTENT: `startpage-calendar-content`,
  STARTPAGE_EVENTLOG_CONTENT: `startpage-eventlog-content`,
  STARTPAGE_BULLETINBOARD_CONTENT: `startpage-bulletin-board-content`,
}

function handleBulletinboardPusherEvent(
  _event:
    | {
        data?:
          | {
              type: `comment` | `thread`
              thread_id: number
              comment_id: number
              event: `changed` | `deleted` | `liked`
            }
          | undefined
      }
    | undefined
): void {
  queryClient.invalidateQueries({ queryKey: [BULLETIN_BOARD_THREADS_QUERY_KEY] })
}

const bulletinInfo: Array<Step> = [
  {
    target: widgetConstants.STARTPAGE_BULLETINBOARD_CONTENT,
    content: `Did anything new happen at the plant or is there anything you think your colleagues need to know? On the bulletin board you can post messages (and comment on others') to all permitted users at the plant. The bulletin board also shows created and edited deviation settings to increase transparency and ease of communication between the users.`,
    title: `Bulletin board for current plant`,
  },
]

const eventlogInfo: Array<Step> = [
  {
    target: widgetConstants.STARTPAGE_EVENTLOG_CONTENT,
    content: `The system events widget shows all events that occur in the system for the current plant. This log contains events split into different types of optimizations, settings as well as outcome values and forecast updates. You can also filter events based on what you would like to see.`,
    title: `Event log`,
  },
]

export default function StartpageView(): ReactElement {
  const { t } = useTranslation()

  const [runTour, setRunTour] = useState(false)
  const { systemId, activeSystem } = useAuth()

  const { channel } = useChannel(systemId ? `aurora-django-bulletinboard-${systemId}` : undefined)

  useEvent(channel, `bulletinboard-event`, handleBulletinboardPusherEvent)

  const isDigitalTwinSystem = !!activeSystem?.primary_digital_twin

  const steps: Array<Step> = isDigitalTwinSystem ? [...bulletinInfo] : [...bulletinInfo, ...eventlogInfo]

  return (
    <View
      header={
        <ModuleHeader
          infoHeader={t(`Startpage`)}
          onInfoButtonClick={() => {
            setRunTour(true)
          }}
        >
          <StartpageHeaderContent />
        </ModuleHeader>
      }
    >
      <SystemMessageBanner module="startpage" />
      <Tour isSingle={false} run={runTour} setRunState={setRunTour} steps={steps} />
      <Grid
        size={{
          xs: 12,
          md: 12,
          lg: isDigitalTwinSystem ? 12 : 8,
          xl: isDigitalTwinSystem ? 12 : 9,
        }}
      >
        <Card
          tourId={widgetConstants.STARTPAGE_BULLETINBOARD_CONTENT}
          title={
            activeSystem
              ? t('Bulletin board for {{currentSystem}}', {
                  currentSystem: activeSystem ? getDisplayName(activeSystem) : null,
                })
              : ''
          }
        >
          <BulletinBoard />
        </Card>
      </Grid>
      {!isDigitalTwinSystem ? (
        <Grid
          size={{
            xs: 12,
            md: 12,
            lg: 4,
            xl: 3,
          }}
        >
          <Card tourId={widgetConstants.STARTPAGE_EVENTLOG_CONTENT} title={t('System events')}>
            <EventLogger />
          </Card>
        </Grid>
      ) : null}
    </View>
  )
}
